/**
 * What a farming patch offers you, worked out from your own plot (the server decides; this is so
 * tooltips and menus say the right thing). Times use the server's clock: `now()` below.
 */
import {
  COMPOSTS, CROPS, CROP_IDS, ITEMS, PATCHES, PESTS, SKILL_NAMES, bandName, bestTool, growsIn, fmtWait, invCount, isPatchKind, level, plotKey, plotLine, plotView, soilWord,
  type Band, type CharacterState, type CropId, type FarmOp, type MapObject, type PestKind, type PlotState,
} from '@sunderfen/shared';

let skew = 0;
/** The server's clock as of the welcome message. */
export function setServerNow(ms: number) { skew = ms - Date.now(); }
export const now = () => Date.now() + skew;

/** A pest at one of your patches right now (from the server's events), until a time. */
export const pests = new Map<number, { critter: PestKind; until: number }>();

export const plotOf = (c: Pick<CharacterState, 'plots'>, o: MapObject): PlotState | undefined => c.plots?.[plotKey(o)];
const patchName = (o: MapObject) => (isPatchKind(o.kind) ? PATCHES[o.kind].name : 'Patch');
const pestAt = (o: MapObject) => { const p = pests.get(o.id); return p && p.until > performance.now() ? p : null; };

/** Seeds in the pack this character can sow here, best first. */
export function sowable(c: CharacterState, o: MapObject, band?: Band): CropId[] {
  const L = level(c, 'farming');
  return CROP_IDS.filter(id => CROPS[id].patch === o.kind && growsIn(CROPS[id], band) && invCount(c, CROPS[id].seed) >= CROPS[id].seeds && L >= CROPS[id].level).sort((a, b) => CROPS[b].level - CROPS[a].level);
}
/** Felling a grown tree: what's missing. */
function fellReq(c: CharacterState, id: CropId) {
  const d = CROPS[id];
  if (d.harvestTool && !bestTool(c, d.harvestTool)) return 'a hatchet';
  if (d.harvestSkill && d.harvestLevel && level(c, d.harvestSkill) < d.harvestLevel) return `${SKILL_NAMES[d.harvestSkill]} ${d.harvestLevel}`;
  return '';
}

/** The tooltip: what a click will do, and what's missing. */
export function patchLabel(c: CharacterState, o: MapObject, band?: Band): [string, string] {
  const s = plotOf(c, o), v = plotView(s, now()), name = patchName(o), pest = pestAt(o);
  if (pest) return [`${PESTS[pest.critter].verb}!`, ''];
  if (!v.crop) {
    if (v.weeds) return [`Hoe the weeds · ${name}`, bestTool(c, 'hoe') ? '' : 'a hoe'];
    const sw = sowable(c, o, band)[0];
    return sw ? [`Plant ${CROPS[sw].plural} · ${name} · soil ${soilWord(v.soil)}`, ''] : [`${name} · clear, ready to plant`, 'seed'];
  }
  const d = CROPS[v.crop];
  if (v.dead) return [`Dig up the dead ${d.plural}`, bestTool(c, 'hoe') ? '' : 'a hoe'];
  if (v.diseased) return [`Treat the diseased ${d.plural}`, invCount(c, 'plant_cure') ? '' : 'plant cure'];
  if (v.ready && d.checkXp && !s?.checked) return [`Check the ${d.name.toLowerCase()} over`, ''];
  if (v.ready && d.harvestTool) return [`Fell your ${d.name.toLowerCase()}`, fellReq(c, v.crop)];
  if (v.ready) return [`Pick ${d.plural}`, ''];
  if (v.weedyNow) return [`Hoe the weeds among the ${d.plural}`, bestTool(c, 'hoe') ? '' : 'a hoe'];
  if (!v.wetNow && bestTool(c, 'can') && c.water > 0) return [`Water the ${d.plural} · ready in ${fmtWait(v.readyIn)}`, ''];
  return [`${d.name} · stage ${v.stage + 1} of ${d.stages}${v.wetNow ? ', watered' : ''}${s?.protected ? ', watched' : s?.scarecrow ? ', scarecrow' : ''} · ready in ${fmtWait(v.readyIn)}`, ''];
}

export interface PatchOption { label: string; sub?: string; op: FarmOp; crop?: CropId }
/** Everything you can do at the patch, for the right-click menu. */
export function patchOptions(c: CharacterState, o: MapObject, band?: Band): PatchOption[] {
  const s = plotOf(c, o), v = plotView(s, now()), out: PatchOption[] = [], pest = pestAt(o);
  if (pest) out.push({ label: PESTS[pest.critter].verb, op: 'shoo' });
  const compost = () => {
    let tier = 0; for (let t = 3; t >= 1 && !tier; t--) if (invCount(c, COMPOSTS[t].item!)) tier = t;
    if ((s?.compost ?? 0) < Math.max(1, tier)) out.push({ label: 'Dig in compost', sub: tier ? `${COMPOSTS[tier].name}: less disease, a bigger crop` : 'make it from weeds in a compost bin', op: 'compost' });
  };
  if (!v.crop) {
    if (v.weeds) out.push({ label: 'Hoe the weeds', sub: `${v.weeds} to go`, op: 'weed' });
    else {
      const L = level(c, 'farming');
      for (const id of CROP_IDS.filter(id => CROPS[id].patch === o.kind)) {
        const d = CROPS[id], have = invCount(c, d.seed);
        if (have < d.seeds && L < d.level) continue;
        if (!growsIn(d, band)) { if (have) out.push({ label: `Plant ${d.plural}`, sub: `only grows in ${bandName(d.minBand!)}`, op: 'plant', crop: id }); continue; }
        out.push({ label: `Plant ${d.plural}`, sub: L < d.level ? `Farming ${d.level}` : have < d.seeds ? `needs ${d.seeds} ${ITEMS[d.seed].name.toLowerCase()}` : `${have} ${ITEMS[d.seed].name.toLowerCase()}${s?.last === id ? ' · same as last time' : ''}`, op: 'plant', crop: id });
      }
      compost();
    }
  } else {
    const d = CROPS[v.crop];
    if (v.dead) out.push({ label: `Dig up the dead ${d.plural}`, op: 'clear' });
    else {
      if (v.diseased) out.push({ label: `Treat the ${d.plural}`, sub: invCount(c, 'plant_cure') ? 'plant cure' : 'needs plant cure (Marlow)', op: 'cure' });
      if (v.ready) out.push(d.checkXp && !s?.checked ? { label: `Check the ${d.name.toLowerCase()} over`, sub: `${d.checkXp.toLocaleString()} Farming xp`, op: 'harvest' } : d.harvestTool ? { label: `Fell your ${d.name.toLowerCase()}`, sub: fellReq(c, v.crop) || ITEMS[d.produce].name.toLowerCase(), op: 'harvest' } : { label: `Pick ${d.plural}`, op: 'harvest' });
      if (v.weedyNow) out.push({ label: 'Hoe the weeds out', sub: 'or lose a pick', op: 'weed' });
      if (!v.ready && !v.diseased && !v.wetNow) out.push({ label: `Water the ${d.plural}`, sub: !bestTool(c, 'can') ? 'needs a watering can' : c.water > 0 ? `${c.water} left in the can` : 'the can is empty', op: 'water' });
      if (!v.ready && !s?.scarecrow && !s?.protected && invCount(c, 'scarecrow')) out.push({ label: 'Stand a scarecrow in it', sub: 'no pests while it grows', op: 'scarecrow' });
      if (!v.ready && !v.diseased && !s?.protected) { const [item, n] = d.protect; out.push({ label: 'Pay Marlow to watch it', sub: `${n} ${ITEMS[item].name.toLowerCase()}: no disease, no pests`, op: 'protect' }); }
      if (v.stage === 0 && !v.ready) compost();
      out.push({ label: `Dig up the ${d.plural}`, op: 'clear' });
    }
  }
  return out;
}

/** Examine text for a patch. */
export const examinePatch = (c: CharacterState, o: MapObject) => `${isPatchKind(o.kind) ? PATCHES[o.kind].desc : 'A patch.'} ${plotLine(plotOf(c, o), now())}`;
